import { createContext, useContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useSessionContext } from '@supabase/auth-helpers-react';

import { supabase } from '@/lib/supabase';
import { EHUserAppMetadata } from '@/lib/types';
import { USER_EVENTS, useTracking } from '@/hooks/useTracking';

interface GemBalance {
  monthly: number;
  purchased: number;
  total: number;
}

interface GemBalanceContextType {
  balance: GemBalance;
  loading: boolean;
  error: string | null;
  refreshBalance: () => Promise<GemBalance | undefined>;
}

const DEFAULT_BALANCE = { monthly: 0, purchased: 0, total: 0 };

const GemBalanceContext = createContext<GemBalanceContextType | undefined>(undefined);

export const GemBalanceProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useSessionContext();
  const [balance, setBalance] = useState<GemBalance>(DEFAULT_BALANCE);
  const [loading, setLoading] = useState(true);
  const isInitRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useTracking();

  /**
   * Refresh gem balance with fresh user from supabase
   * @returns new balance or undefined if no change
   */
  const refreshBalance = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await supabase.auth.getUser();
      if (!data.user) {
        setBalance(DEFAULT_BALANCE);
        return DEFAULT_BALANCE;
      }

      const appMetadata = data.user.app_metadata as EHUserAppMetadata;
      const monthlyBalance = appMetadata.gems_monthly_balance ?? 0;
      const purchasedBalance = appMetadata.gems_purchased_balance ?? 0;
      const total = monthlyBalance + purchasedBalance;

      const newBalance = {
        monthly: monthlyBalance,
        purchased: purchasedBalance,
        total,
      };
      
      setBalance(newBalance);
      return newBalance;
    } catch (err) {
      setError('Failed to refresh gem balance');
      trackEvent(USER_EVENTS.USER_ERROR, {
        error: err,
        source: 'useGemBalance',
      });
    } finally {
      setLoading(false);
    }
    // omit trackEvent dependency to avoid useless re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setBalance(DEFAULT_BALANCE);
      setLoading(false);
      return;
    }
    // init only once
    if (isInitRef.current) return;
    // Fetch from server to avoid stale data from local supabase session
    // (local storage staying unchanged even after auth.getUser())
    void refreshBalance();
    isInitRef.current = true;
  }, [refreshBalance, session?.user]);

  const value = {
    balance,
    loading,
    error,
    refreshBalance,
  };

  return (
    <GemBalanceContext.Provider value={value}>
      {children}
    </GemBalanceContext.Provider>
  );
};

export const useGemBalance = () => {
  const context = useContext(GemBalanceContext);
  
  if (context === undefined) {
    throw new Error('useGemBalance must be used within a GemBalanceProvider');
  }
  
  return context;
}; 